import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Order } from '../../../shared/models/Order';
import { emptyOrderAdminKpis, OrderAdminKpisDto, OrderAdminQuery, OrderService } from '../../../core/services/order.service';
import { EstadosService } from '../../../core/services/estados.service';
import { OrigenVentaService } from '../../../core/services/origen-venta.service';
import { MessageService } from 'primeng/api';
import { Estado } from '../../../shared/models/system';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

import { PeruDatePipe } from '../../../shared/pipes/peru-date.pipe';
import { formatMoananiDateTimeExport } from '../../../core/utils/peru-datetime';
import {
  AdminExportBundle,
  boletaMapFromOrders,
  boletaStatusFromOrder,
  historyBounds,
  isTerminalOrderStatus,
  loadAdminExport,
  mergeOrderRow,
  orderIdsWithoutBoletaFlag
} from '../../../core/utils/admin-order-page';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BoletaService, OrderBoletaStatus } from '../../../core/services/boleta.service';
import {
  getBoletaExportLabel,
  getOrderAmountPaid as exportAmountPaid,
  getOrderDeliveryPrice as exportDeliveryPrice,
  getOrderItemsCount as exportItemsCount,
  getOrderCategoryLabel as exportCategoryLabel,
  getOrderModeloLabel as exportModeloLabel,
  getOrderProductLabel as exportProductLabel,
  getOrderTotalAmount as exportTotalAmount,
  money
} from '../../../core/utils/order-export';
import { exportManualSalesExcel } from '../../../core/utils/sales-history-excel';

@Component({
  selector: 'app-orders-history',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    TooltipModule,
    TagModule,
    CurrencyPipe,
    DialogModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    SelectModule,
    MultiSelectModule,
    IconFieldModule,
    InputIconModule,
    PeruDatePipe,
  ],
  providers: [MessageService],
  templateUrl: './orders-history.component.html',
  styleUrl: './orders-history.component.css',
})
export class OrdersHistoryComponent implements OnInit {

  orders: Order[] = [];
  filteredOrders: Order[] = [];
  loading: boolean = true;
  first = 0;
  rows = 10;
  totalRecords = 0;
  search = '';
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  viewDialogVisible: boolean = false;
  selectedOrderForView: Order | null = null;
  statusDialogVisible: boolean = false;
  selectedOrderForStatus: Order | null = null;
  selectedOrders: Order[] = [];
  isBulkStatus: boolean = false;
  selectedEstadoIds: number[] = [];
  estadoOptions: Estado[] = [];

  boletaStatusMap: Record<number, OrderBoletaStatus> = {};
  emittingBoletaOrderId: number | null = null;

  dniDialog = false;
  dniOrder: Order | null = null;
  dniInput = '';
  dniSubmitted = false;
  savingDni = false;
  emitWithoutDniDialog = false;
  emitWithoutDniOrder: Order | null = null;

  private kpis: OrderAdminKpisDto = emptyOrderAdminKpis();
  private kpiRequest = 0;
  private listRequest = 0;
  exporting: 'excel' | 'pdf' | null = null;
  dateRangeOptions = [
    { label: 'Todo el historial', value: 'all' },
    { label: 'Últimos 7 días', value: '7' },
    { label: 'Últimos 15 días', value: '15' },
    { label: 'Últimos 30 días', value: '30' }
  ];
  selectedRange: string = 'all';
  selectedEstadoFilterId: number | null = null;
  estadoFilterOptions: { label: string; value: number | null }[] = [{ label: 'Todos los estados', value: null }];

  origenVentaFilterId: number | null = null;
  origenVentaFilterOptions: { label: string; value: number | null }[] = [{ label: 'Todos los orígenes', value: null }];

  constructor(
    private orderService: OrderService,
    private estadosService: EstadosService,
    private origenVentaService: OrigenVentaService,
    private boletaService: BoletaService,
    private cd: ChangeDetectorRef,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadKpis();
    this.estadosService.getActive().subscribe({
      next: (list) => {
        this.estadoOptions = list;
        this.estadoFilterOptions = [
          { label: 'Todos los estados', value: null },
          ...list.map(e => ({ label: e.nombreEstado, value: e.id }))
        ];
        this.cd.detectChanges();
      },
      error: () => {}
    });
    this.origenVentaService.getAll().subscribe({
      next: (list) => {
        this.origenVentaFilterOptions = [
          { label: 'Todos los orígenes', value: null },
          ...list.map(o => ({ label: o.nombreOrigenVenta, value: o.id }))
        ];
        this.cd.detectChanges();
      },
      error: () => {}
    });
  }

  scheduleServerLoad(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.reloadFirst(), 350);
  }

  onDateRangeChange(): void {
    this.reloadFirst();
  }

  reloadFirst(): void {
    this.first = 0;
    this.loadKpis();
    this.loadOrders({ first: 0, rows: this.rows });
  }

  get totalRevenue(): number {
    return this.kpis.cobradoPeriodo;
  }

  get totalVentas(): number {
    return this.kpis.totalVentasEntregadas;
  }

  get totalEnvioInfo(): number {
    return this.kpis.totalEnvioEntregadas;
  }

  private listFilters(): OrderAdminQuery {
    return {
      canal: 'MANUAL',
      isManualSale: true,
      vista: 'HISTORIAL',
      q: this.search?.trim() || undefined,
      origenVentaId: this.origenVentaFilterId ?? undefined,
      estadoIds: this.selectedEstadoFilterId != null ? [this.selectedEstadoFilterId] : undefined,
      ...historyBounds(this.selectedRange)
    };
  }

  loadKpis(): void {
    const request = ++this.kpiRequest;
    this.orderService.getAdminKpis(this.listFilters()).subscribe({
      next: (kpis) => {
        if (request !== this.kpiRequest) return;
        this.kpis = kpis;
        this.cd.detectChanges();
      },
      error: () => {
        if (request !== this.kpiRequest) return;
        this.kpis = emptyOrderAdminKpis();
        this.cd.detectChanges();
      }
    });
  }

  openStatusDialog(order: Order) {
    this.isBulkStatus = false;
    this.selectedOrderForStatus = order;
    this.selectedOrders = [];
    this.selectedEstadoIds = order.estados?.map(e => e.id) ?? [];
    this.statusDialogVisible = true;
  }

  openBulkStatusDialog() {
    if (!this.selectedOrders?.length) {
      this.messageService.add({ severity: 'warn', summary: 'Selección', detail: 'Selecciona al menos un pedido' });
      return;
    }
    this.isBulkStatus = true;
    this.selectedOrderForStatus = null;
    this.selectedEstadoIds = [];
    this.statusDialogVisible = true;
  }

  saveStatus() {
    if (this.selectedEstadoIds.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'Selección', detail: 'Elige al menos un estado' });
      return;
    }
    if (this.isBulkStatus && this.selectedOrders?.length) {
      let completed = 0;
      const total = this.selectedOrders.length;
      this.selectedOrders.forEach(order => {
        this.orderService.updateEstados(order.id, this.selectedEstadoIds).subscribe({
          next: (updated) => {
            this.applyUpdated(updated);
            completed++;
            if (completed === total) {
              this.loadKpis();
              this.loadOrders({ first: this.first, rows: this.rows });
              this.messageService.add({ severity: 'success', summary: 'Actualizado', detail: `Estados actualizados en ${total} pedido(s)` });
              this.statusDialogVisible = false;
              this.isBulkStatus = false;
              this.selectedOrders = [];
              this.cd.detectChanges();
            }
          },
          error: () => {
            completed++;
            if (completed === total) {
              this.loadKpis();
              this.loadOrders({ first: this.first, rows: this.rows });
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Algunos pedidos no se pudieron actualizar' });
              this.statusDialogVisible = false;
              this.isBulkStatus = false;
              this.cd.detectChanges();
            }
          }
        });
      });
    } else if (this.selectedOrderForStatus) {
      this.orderService.updateEstados(this.selectedOrderForStatus.id, this.selectedEstadoIds).subscribe({
        next: (updated) => {
          this.messageService.add({ severity: 'success', summary: 'Actualizado', detail: 'Estados actualizados' });
          this.statusDialogVisible = false;
          this.applyUpdated(updated);
          this.loadKpis();
          this.loadOrders({ first: this.first, rows: this.rows });
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo actualizar' })
      });
    }
  }

  getOrderProductLabel(order: Order): string {
    return exportProductLabel(order);
  }

  getOrderItemsCount(order: Order): number {
    return exportItemsCount(order);
  }

  getEstadosNames(estados: any[] | undefined): string {
    if (!estados?.length) return '';
    return estados.map((e: any) => e.nombreEstado).filter(Boolean).join(', ');
  }

  loadOrders(event?: TableLazyLoadEvent) {
    const request = ++this.listRequest;
    this.loading = true;
    const first = event?.first ?? this.first;
    const rows = event?.rows || this.rows;
    this.first = first || 0;
    this.rows = rows;
    const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
    this.orderService.getAdminOrders({
      ...this.listFilters(),
      page,
      size: this.rows
    }).subscribe({
      next: (res) => {
        if (request !== this.listRequest) return;
        this.orders = res.content || [];
        this.filteredOrders = this.orders;
        this.totalRecords = res.totalElements || 0;
        this.loading = false;
        this.syncBoletas(this.orders.filter(o => this.esVentaManualEntregada(o)));
        this.cd.detectChanges();
      },
      error: (err) => {
        if (request !== this.listRequest) return;
        console.error(err);
        this.loading = false;
        this.orders = [];
        this.filteredOrders = [];
        this.totalRecords = 0;
        this.cd.detectChanges();
      }
    });
  }

  private applyUpdated(updated: Order): void {
    const drop = !!updated.status && !isTerminalOrderStatus(updated.status);
    const result = mergeOrderRow(this.orders, updated, drop);
    this.orders = result.rows;
    if (result.removed) this.totalRecords = Math.max(0, this.totalRecords - 1);
    this.selectedOrders = drop
      ? this.selectedOrders.filter(order => order.id !== updated.id)
      : this.selectedOrders.map(order => order.id === updated.id ? { ...order, ...updated } : order);
    if (this.selectedOrderForView?.id === updated.id) {
      this.selectedOrderForView = drop ? null : { ...this.selectedOrderForView, ...updated };
      if (drop) this.viewDialogVisible = false;
    }
    const flag = boletaStatusFromOrder(updated);
    if (flag) this.boletaStatusMap[updated.id] = flag;
    if (drop) delete this.boletaStatusMap[updated.id];
    this.filteredOrders = this.orders;
    this.cd.detectChanges();
  }

  private formatDateTimeExport(isoStr: string): string {
    return formatMoananiDateTimeExport(isoStr || '');
  }

  exportExcel() {
    this.runExport('excel', ({ rows, kpis, boletas }) => exportManualSalesExcel(rows, boletas, {
      cobrado: kpis.cobradoPeriodo,
      ventas: kpis.totalVentasEntregadas,
      envio: kpis.totalEnvioEntregadas
    }));
  }

  exportPdf() {
    this.runExport('pdf', ({ rows, kpis, boletas }) => {
      const doc = new jsPDF({ orientation: 'landscape' });

      doc.setFontSize(16);
      doc.text('Reporte de Historial de Ventas', 14, 20);
      doc.setFontSize(10);
      doc.text(`Generado: ${new Date().toLocaleDateString()} - Filtro: ${this.selectedRange === 'all' ? 'Histórico Completo' : 'Últimos ' + this.selectedRange + ' días'}`, 14, 28);

      const head = [['Fecha reg.', 'N°', 'Cliente', 'Producto', 'Categoría', 'Modelo', 'Estado', 'Envío', 'Adelanto', 'Total venta', 'Boleta']];
      const data = rows.map(o => [
        this.formatDateTimeExport(o.createdAt || ''),
        o.id,
        o.clientName,
        exportProductLabel(o),
        exportCategoryLabel(o),
        exportModeloLabel(o),
        o.estados?.map((e: any) => e.nombreEstado).join(', ') || o.status,
        money(exportDeliveryPrice(o)),
        money(exportAmountPaid(o)),
        money(exportTotalAmount(o)),
        getBoletaExportLabel(o.id, boletas)
      ]);

      autoTable(doc, {
        startY: 35,
        head: head,
        body: data,
        theme: 'grid',
        headStyles: { fillColor: [0, 0, 0] },
        styles: { fontSize: 6 },
        columnStyles: { 3: { cellWidth: 32 }, 4: { cellWidth: 22 }, 5: { cellWidth: 22 }, 10: { cellWidth: 32 } }
      });

      const finalY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total cobrado (adelantos/pagados): ${money(kpis.cobradoPeriodo)}`, 14, finalY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Total ventas: ${money(kpis.totalVentasEntregadas)}  |  Envío (informativo, no suma): ${money(kpis.totalEnvioEntregadas)}`, 14, finalY + 7);

      doc.save('historial_ventas.pdf');
    });
  }

  private runExport(
    kind: 'excel' | 'pdf',
    consume: (bundle: AdminExportBundle) => Promise<void> | void
  ): void {
    if (this.exporting) return;
    const summary = kind === 'excel' ? 'Excel' : 'PDF';
    this.exporting = kind;
    this.cd.detectChanges();
    loadAdminExport(this.orderService, this.boletaService, this.listFilters()).subscribe({
      next: (bundle) => {
        if (!bundle.rows.length) {
          this.exporting = null;
          this.messageService.add({ severity: 'warn', summary, detail: 'No hay pedidos para exportar' });
          this.cd.detectChanges();
          return;
        }
        Promise.resolve()
          .then(() => consume(bundle))
          .catch((e: Error) => this.messageService.add({
            severity: 'warn',
            summary,
            detail: e?.message || 'No se pudo exportar'
          }))
          .finally(() => {
            this.exporting = null;
            this.cd.detectChanges();
          });
      },
      error: () => {
        this.exporting = null;
        this.messageService.add({ severity: 'error', summary, detail: 'No se pudo exportar' });
        this.cd.detectChanges();
      }
    });
  }

  openViewDialog(order: Order) {
    this.selectedOrderForView = order;
    this.viewDialogVisible = true;
  }

  copyToClipboard(text: string): void {
    if (!text?.trim()) return;
    navigator.clipboard.writeText(text.trim()).then(() => {
      this.messageService.add({ severity: 'success', summary: 'Copiado', detail: 'Texto copiado al portapapeles', life: 2000 });
    }).catch(() => {
      this.messageService.add({ severity: 'warn', summary: 'Error', detail: 'No se pudo copiar', life: 2000 });
    });
  }

  getFullAddress(order: Order): string {
    const distrito = order.distrito?.nombre || order.district || '';
    const address = order.deliveryAddress || '';
    return distrito && address ? `${address}, ${distrito}` : address || distrito;
  }

  getWhatsAppLink(phone: string): string {
    const num = (phone || '').replace(/\D/g, '');
    const pref = num.length <= 9 ? '51' : '';
    return `https://wa.me/${pref}${num}`;
  }

  calculateItemsSubtotal(order: any): number {
    if (!order || !order.items) return 0;
    return order.items.reduce((acc: number, item: any) => {
      const price = item.unitPrice || item.product?.price || 0;
      const itemSubtotal = (item.subTotal && item.subTotal > 0) ? item.subTotal : (price * (item.quantity || 1));
      return acc + itemSubtotal;
    }, 0);
  }

  getSeverity(status: string): "success" | "secondary" | "info" | "warn" | "danger" | "contrast" | undefined {
    switch (status) {
      case 'PAGO COMPLETO': return 'success';
      case 'ADELANTO': return 'warn';
      case 'PENDIENTE DE PAGO': return 'danger';
      case 'EN VERIFICACION': return 'info';
      case 'ENTREGADO': return 'success';
      case 'EN RUTA': return 'info';
      case 'CONFIRMADO': return 'secondary';
      case 'CANCELADO': return 'danger';
      default: return 'secondary';
    }
  }

  esVentaManualEntregada(order: Order): boolean {
    return order.isManualSale === true && order.status === 'ENTREGADO';
  }

  private safeMoney(v: unknown): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  getOrderAmountPaid(order: Order): number {
    return exportAmountPaid(order);
  }

  getOrderTotalAmount(order: Order): number {
    return exportTotalAmount(order);
  }

  getOrderPendientePagar(order: Order): number {
    return Math.max(0, exportTotalAmount(order) - exportAmountPaid(order));
  }

  isPagoCompleto(order: Order): boolean {
    const st = (order.paymentStatus || '').toUpperCase();
    if (st === 'PAGO_COMPLETO' || st === 'PAGADO_COMPLETO') return true;
    return this.getOrderPendientePagar(order) <= 0 && this.getOrderAmountPaid(order) > 0;
  }

  private syncBoletas(orders: Order[]): void {
    this.boletaStatusMap = boletaMapFromOrders(orders);
    const missing = orderIdsWithoutBoletaFlag(orders);
    this.cd.detectChanges();
    if (!missing.length) return;
    this.boletaService.estadosOrdenes(missing).subscribe({
      next: (rows) => {
        rows.forEach(r => { this.boletaStatusMap[r.orderId] = r; });
        this.cd.detectChanges();
      }
    });
  }

  tieneBoleta(orderId: number): boolean {
    return !!this.boletaStatusMap[orderId]?.tieneBoleta;
  }

  tieneDocumentoCliente(order: Order): boolean {
    const doc = String(order.dni ?? '').trim();
    if (!doc || doc === 'null' || doc === 'undefined') return false;
    return /^\d{8}$/.test(doc) || /^[A-Za-z0-9]{8,12}$/.test(doc);
  }

  puedeEmitirBoleta(order: Order): boolean {
    return this.esVentaManualEntregada(order)
      && this.isPagoCompleto(order)
      && !this.tieneBoleta(order.id);
  }

  mensajeEmitirBoleta(order: Order): string {
    if (!this.esVentaManualEntregada(order)) return '';
    if (this.tieneBoleta(order.id)) return '';
    if (!this.isPagoCompleto(order)) {
      const pendiente = this.getOrderPendientePagar(order);
      return pendiente > 0
        ? `Falta registrar S/ ${pendiente.toFixed(2)} para emitir boleta`
        : 'Registre el pago completo para emitir boleta';
    }
    if (!this.tieneDocumentoCliente(order)) {
      return 'Se pedirá confirmación: el cliente no tiene DNI/CE';
    }
    return 'Emitir boleta de venta';
  }

  openDniDialog(order: Order): void {
    this.dniOrder = order;
    this.dniInput = String(order.dni ?? '').trim();
    this.dniSubmitted = false;
    this.dniDialog = true;
  }

  guardarDniPedido(): void {
    this.dniSubmitted = true;
    const dni = this.dniInput?.trim();
    if (!this.dniOrder || !dni || !this.esDocumentoValido(dni)) return;

    this.savingDni = true;
    this.orderService.updateDocumento(this.dniOrder.id, dni).subscribe({
      next: (updated) => {
        this.applyDniToLocalOrder(this.dniOrder!.id, updated.dni ?? dni);
        this.savingDni = false;
        this.dniDialog = false;
        this.dniOrder = null;
        this.messageService.add({
          severity: 'success',
          summary: 'Documento',
          detail: 'DNI/CE guardado en el pedido. Ya puedes emitir la boleta.'
        });
        this.cd.detectChanges();
      },
      error: (err) => {
        this.savingDni = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Documento',
          detail: err?.error?.message || 'No se pudo guardar el DNI/CE'
        });
        this.cd.detectChanges();
      }
    });
  }

  private applyDniToLocalOrder(orderId: number, dni: string | number): void {
    const apply = (list: Order[]) => {
      const idx = list.findIndex(o => o.id === orderId);
      if (idx >= 0) list[idx] = { ...list[idx], dni };
    };
    apply(this.orders);
    apply(this.filteredOrders);
    if (this.selectedOrderForView?.id === orderId) {
      this.selectedOrderForView = { ...this.selectedOrderForView, dni };
    }
  }

  esDocumentoValido(doc: string): boolean {
    return /^\d{8}$/.test(doc) || /^[A-Za-z0-9]{8,12}$/.test(doc);
  }

  emitirBoleta(order: Order): void {
    if (!this.puedeEmitirBoleta(order)) return;
    if (!this.tieneDocumentoCliente(order)) {
      this.emitWithoutDniOrder = order;
      this.emitWithoutDniDialog = true;
      return;
    }
    this.ejecutarEmision(order);
  }

  confirmarEmitirSinDni(): void {
    const order = this.emitWithoutDniOrder;
    this.emitWithoutDniDialog = false;
    this.emitWithoutDniOrder = null;
    if (order) this.ejecutarEmision(order);
  }

  cancelarEmitirEIntroducirDni(): void {
    const order = this.emitWithoutDniOrder;
    this.emitWithoutDniDialog = false;
    this.emitWithoutDniOrder = null;
    if (order) this.openDniDialog(order);
  }

  private ejecutarEmision(order: Order): void {
    this.emittingBoletaOrderId = order.id;
    this.boletaService.emitir(order.id).subscribe({
      next: (boleta) => {
        this.boletaStatusMap[order.id] = {
          orderId: order.id,
          tieneBoleta: true,
          boletaId: boleta.id,
          numeroCompleto: boleta.numeroCompleto
        };
        this.emittingBoletaOrderId = null;
        this.messageService.add({ severity: 'success', summary: 'Boleta', detail: `Emitida ${boleta.numeroCompleto}` });
        this.cd.detectChanges();
      },
      error: (err) => {
        this.emittingBoletaOrderId = null;
        const msg = err?.error?.message || 'No se pudo emitir la boleta';
        this.messageService.add({ severity: 'warn', summary: 'Boleta', detail: msg });
        this.cd.detectChanges();
      }
    });
  }

  descargarBoleta(orderId: number): void {
    const st = this.boletaStatusMap[orderId];
    if (!st?.boletaId) return;
    this.boletaService.descargarPdf(st.boletaId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${st.numeroCompleto || 'boleta'}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'PDF', detail: 'No se pudo descargar' })
    });
  }
}